/**
 * Excel-Import-Dialog für Fahrzeuge.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader2, Upload, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { vehiclesAPI } from '../../lib/api';

export const VehicleImportDialog = ({ open, onOpenChange, onImported }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setResult(null); };

  const onSubmit = async () => {
    if (!file) return;
    setImporting(true);
    setResult(null);
    try {
      const r = await vehiclesAPI.importFromExcel(file);
      setResult(r.data);
      toast.success(`${r.data.created_count} angelegt, ${r.data.updated_count} aktualisiert`);
      onImported?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import fehlgeschlagen');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Fahrzeuge aus Excel importieren
          </DialogTitle>
          <DialogDescription>
            Spalten werden automatisch erkannt: Kennzeichen, Marke, Modell, Halter, Standort,
            Tankkarte, TÜV, Kilometerstand u.v.m. Bestehende Fahrzeuge (Kennzeichen identisch)
            werden aktualisiert, neue angelegt.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="import-file"
          />

          {result && (
            <Alert className={result.error_count ? 'border-amber-500/40' : 'border-green-500/40'}>
              {result.error_count ? <AlertCircle className="h-4 w-4 text-amber-600" /> : <CheckCircle2 className="h-4 w-4 text-green-600" />}
              <AlertDescription>
                <p className="font-medium">
                  ✅ {result.created_count} neu · 🔄 {result.updated_count} aktualisiert
                  {result.error_count > 0 ? ` · ❌ ${result.error_count} Fehler` : ''}
                </p>
                {result.detected_columns?.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Erkannte Spalten: {result.detected_columns.slice(0, 10).join(', ')}{result.detected_columns.length > 10 ? '…' : ''}
                  </p>
                )}
                {result.errors?.length > 0 && (
                  <ul className="text-xs mt-2 max-h-32 overflow-y-auto">
                    {result.errors.map((e, i) => (
                      <li key={`err-${e.row}-${i}`}>Zeile {e.row}{e.plate ? ` (${e.plate})` : ''}: {e.error}</li>
                    ))}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>Schließen</Button>
          <Button onClick={onSubmit} disabled={!file || importing} data-testid="btn-do-import">
            {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            Importieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
